import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  HeartPulse,
  Activity,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  RelatedPages,
  allEducationLinks,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
const DepressionHub = () => {
  const subPages = [
    {
      title: "About Depression",
      path: "/contents/conditions/depression/about-depression/",
      description:
        "Understand the biological basis, scope, and neural network mechanisms behind depression, affecting over 16 million American adults.",
      icon: Brain,
    },
    {
      title: "Depression Symptoms",
      path: "/contents/conditions/depression/depression-symptoms/",
      description:
        "Explore standard DSM-V clinical criteria including persistent sadness, appetite changes, sleep disruption, fatigue, and cognitive effects.",
      icon: Activity,
    },
    {
      title: "Treating Depression",
      path: "/contents/conditions/depression/treating-depression/",
      description:
        "Compare traditional antidepressants and psychotherapy with advanced non-invasive options like TMS, SAINT®, SPRAVATO®, and IV Ketamine.",
      icon: HeartPulse,
    },
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Depression Overview & Treatment | Neuro Wellness TMS Centers of America"
        }
        description={
          "Comprehensive information on major depressive disorder, symptoms, traditional vs. alternative treatments, and advanced neuromodulation in Coral Springs, FL."
        }
        canonical={"/contents/conditions/depression"}
        keywords={
          "Depression Coral Springs, major depressive disorder South Florida, depression symptoms, treating depression TMS, SAINT TMS depression, Spravato esketamine, brain medicine Florida"
        }
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* Hero Header */}
        <section className="relative overflow-hidden bg-gradient-navy text-primary-foreground py-16 sm:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px mx-auto max-w-7xl">
            <div className="max-w-3xl space-y-4 animate-fade-up">
              <span className="inline-block rounded-full bg-accent/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-accent uppercase tracking-wider">
                Conditions • Mental Health
              </span>
              <h1 className="font-headings text-3xl font-extrabold text-white sm:text-5xl md:text-6xl">
                Depression
              </h1>
              <p className="text-lg sm:text-2xl text-accent font-semibold">
                Comprehensive Knowledge, Diagnosis & Advanced Neuromodulation
                Treatments
              </p>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-2xl">
                At Neuro Wellness TMS Centers of America in Coral Springs, FL,
                we specialize in helping individuals break free from
                depression—especially when traditional antidepressants have not
                provided adequate relief.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="#consultation">Schedule Consultation</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white bg-transparent hover:bg-white/10"
                >
                  <a href="tel:9548279793">Call 954-827-9793</a>
                </Button>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-8 sm:h-12 text-background"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,40 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        </section>

        {/* Subpages Section */}
        <section className="py-12 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-7xl space-y-12">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent">
                <ShieldCheck className="h-4 w-4" />
                Educational Resources
              </span>
              <h2 className="font-headings text-2xl sm:text-4xl font-extrabold text-primary">
                Explore Depression Topics
              </h2>
              <p className="text-muted-foreground text-sm sm:text-base">
                Click a topic below to learn more about the biological causes,
                clinical symptoms, and modern treatment approaches available at
                our center.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {subPages.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col justify-between rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft hover:shadow-premium transition-all group"
                  >
                    <div className="space-y-4">
                      <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-colors">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-headings text-xl font-bold text-primary">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground/80 leading-relaxed font-normal">
                        {item.description}
                      </p>
                    </div>

                    <div className="pt-6">
                      <Button
                        asChild
                        variant="outline"
                        className="w-full justify-between border-primary/20 hover:bg-primary hover:text-white transition-colors"
                      >
                        <Link to={item.path}>
                          <span>Read Overview</span>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Highlights Box */}
            <div className="rounded-2xl bg-light-blue/40 border border-light-blue p-6 sm:p-10 space-y-6">
              <div className="max-w-3xl space-y-3">
                <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                  Innovative Depression Care in South Florida
                </h3>
                <p className="text-sm sm:text-base text-foreground/85 leading-relaxed font-normal">
                  Whether you are seeking diagnostic clarity or have struggled
                  for years with treatment-resistant depression, our
                  board-certified psychiatrists and psychiatric nurse
                  practitioners provide personalized, evidence-based solutions.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                {[
                  "SAINT® 5-Day Protocol",
                  "FDA-Cleared TMS Therapy",
                  "SPRAVATO® REMS Center",
                  "In-Network with Medicare & VA",
                ].map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-sm font-semibold text-primary bg-card p-3.5 rounded-xl border border-border shadow-xs"
                  >
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Consultation Section */}
        <RelatedPages
          heading="Explore Related Resources"
          links={[
            ...allEducationLinks.filter(
              (l) => l.href !== "/contents/conditions/depression/",
            ),
            ...allTreatmentsLinks,
            ...allServiceLinks,
            ...allDecisionLinks,
          ]}
        />
        <Consultation />
      </main>

      <Footer />
    </div>
  );
};
export default DepressionHub;
